import styled, { css, keyframes } from "styled-components";

import { colorsPalettes, setElevation, setFontVariant } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import {
    ONBOARDING_AGENT_TRIGGER_SIZE,
    ONBOARDING_AGENT_VIEWPORT_MARGIN,
    PANEL_Z_INDEX,
} from "../OnboardingAgent/constants";
import { SEND_BUTTON_BORDER_RADIUS_PX, SEND_BUTTON_SIZE_PX } from "../OnboardingAgentComposer/constants";
import { SEND_GRADIENT, SEND_GRADIENT_ACTIVE, SEND_GRADIENT_HOVER } from "../OnboardingAgentComposer/styles";

const NUDGE_TRIGGER_GAP_PX = 12;
const NUDGE_MIN_WIDTH_PX = 320;
const NUDGE_MAX_WIDTH_PX = 460;
const NUDGE_APPEAR_DURATION_MS = 600;

/** Scales up from ~0 with a slight overshoot, anchored at the card's bottom-right corner (closest
 * point to the trigger button), so the card visually pops out of the trigger rather than just fading in. */
const nudgeAppearKf = keyframes`
    from {
        opacity: 0;
        transform: scale(0.4);
    }

    60% {
        opacity: 1;
        transform: scale(1.03);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
`;

type NudgeSlotProps = {
    readonly $x: number;
    readonly $y: number;
};

/** Anchored at the trigger's top-right corner, then pulled up-and-left by its own size so the card's
 * bottom-right corner ends up a small gap above the trigger — matching the Figma layout (no connecting tail). */
export const NudgeSlot = styled.div<NudgeSlotProps>`
    position: fixed;
    left: calc(${({ $x }) => $x}px + ${ONBOARDING_AGENT_TRIGGER_SIZE}px);
    top: ${({ $y }) => $y}px;
    z-index: ${PANEL_Z_INDEX};
    max-width: calc(100vw - ${ONBOARDING_AGENT_VIEWPORT_MARGIN * 2}px);
    transform: translate(-100%, calc(-100% - ${NUDGE_TRIGGER_GAP_PX}px));
`;

export const NudgeCard = styled(Flex)`
    box-sizing: border-box;
    /* Shrink-to-fit the widest child (the longest suggestion pill, via NudgeSuggestionList) so
     * questions never wrap to a second line, bounded by a sensible min/max width. */
    width: max-content;
    min-width: ${NUDGE_MIN_WIDTH_PX}px;
    max-width: min(${NUDGE_MAX_WIDTH_PX}px, calc(100vw - ${ONBOARDING_AGENT_VIEWPORT_MARGIN * 2}px));
    flex-direction: column;
    gap: 12px;
    padding: 24px 16px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 16px;
    background-color: ${colorsPalettes.carbon[0]};
    transform-origin: right bottom;
    animation: ${nudgeAppearKf} ${NUDGE_APPEAR_DURATION_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
    ${setElevation("Z3")}
`;

export const NudgeHeader = styled(Flex)`
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`;

/** Sits inline at the top-right of the header (not floating outside the card, per the latest Figma).
 * The negative margin offsets the button's own hit-area padding so the icon sits flush with the corner. */
export const CloseButton = styled(IconButton)`
    flex-shrink: 0;
    margin: -16px -10px 0 0;
`;

export const NudgeGreetingTitleRow = styled(Flex)`
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

export const NudgeGreetingTitle = styled(Text)`
    min-width: 0;
`;

export const NudgeSuggestionList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

export const NudgeSuggestionItem = styled.button`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    width: 100%;
    padding: 4px 0 4px 4px;
    margin: 0;
    border: none;
    border-radius: 6px;
    background-color: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: ${colorsPalettes.bluegrey[100]};
    }

    &:focus-visible {
        outline: 2px solid ${colorsPalettes.blue[400]};
        outline-offset: 2px;
    }
`;

export const NudgeSuggestionText = styled(Text)`
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

export const NudgeSuggestionIcon = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: ${colorsPalettes.carbon[300]};
`;

export const NudgeInputRow = styled(Flex)`
    box-sizing: border-box;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 8px 4px 16px;
    border: 1px solid ${colorsPalettes.carbon[100]};
    border-radius: 24px;
    background-color: ${colorsPalettes.carbon[0]};
`;

export const NudgeTextInput = styled.input`
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    background: transparent;
    border-color: ${colorsPalettes.carbon[100]};
    ${setFontVariant("body1", colorsPalettes.carbon[400])};

    &::placeholder {
        ${setFontVariant("body1", colorsPalettes.carbon[200])};
    }
`;

export const NudgeInputSendButton = styled.button<{ $canSend: boolean }>`
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: ${SEND_BUTTON_SIZE_PX}px;
    height: ${SEND_BUTTON_SIZE_PX}px;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: ${SEND_BUTTON_BORDER_RADIUS_PX}px;
    cursor: ${({ $canSend }) => ($canSend ? "pointer" : "not-allowed")};
    background-color: ${({ $canSend }) => ($canSend ? "transparent" : colorsPalettes.carbon[100])};

    ${({ $canSend }) =>
        $canSend &&
        css`
            ${SEND_GRADIENT};

            &:hover {
                ${SEND_GRADIENT_HOVER};
            }

            &:active {
                ${SEND_GRADIENT_ACTIVE};
            }
        `}
`;
